/**
 * Template Interstitial Slice
 *
 * Manages flight interstitial template state including:
 * - Selected fares
 * - Selected fare combo
 * - Loading fare ID
 *
 * This slice is specifically for the flights-review-interstitial template.
 * Note: Popup handling is centralized in popupsSlice.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { FlightsReviewInterstitialTemplateData } from '@contracts';
import type { SelectedFareState, SelectedFareCombo } from '../types/flightsTypes';
import {
  fetchFlightInterstitialThunk,
  fetchInterstitialWithFareThunk,
} from '../thunks/interstitialThunks';
import { applyInterstitialDataToState } from '../utils/reviewStateHelpers';
import {
  setActiveTemplate,
  updateTemplateFromStream,
} from '@/features/chat/redux/slices/templateSlice';

// ============================================
// State Types
// ============================================

export interface TemplateInterstitialState {
  selectedFares: SelectedFareState;
  /**
   * Selected fare combo string format
   * Format: "journeyIndex-legIndex-fareId|journeyIndex-legIndex-fareId|..."
   * Example: "0-0-0|1-0-1|2-0-2" represents:
   * - Journey 0, Leg 0, Fare ID "0"
   * - Journey 1, Leg 0, Fare ID "1"
   * - Journey 2, Leg 0, Fare ID "2"
   * Note: Uses fare.fareID instead of array index for consistency
   */
  selectedFareCombo: SelectedFareCombo;
  loadingFareId: string | null;
  /**
   * Cached interstitial template data.
   * Used when navigating back from traveller page to avoid re-fetching.
   */
  templateData: FlightsReviewInterstitialTemplateData | null;
}

// ============================================
// Initial State
// ============================================

export const initialTemplateInterstitialState: TemplateInterstitialState = {
  selectedFares: {},
  selectedFareCombo: '',
  loadingFareId: null,
  templateData: null,
};

// ============================================
// Slice
// ============================================

const templateInterstitialSlice = createSlice({
  name: 'flights/templateInterstitial',
  initialState: initialTemplateInterstitialState,
  reducers: {
    setSelectedFares: (state, action: PayloadAction<SelectedFareState>) => {
      state.selectedFares = action.payload;
    },

    setSelectedFareCombo: (state, action: PayloadAction<SelectedFareCombo>) => {
      state.selectedFareCombo = action.payload;
    },

    setLoadingFareId: (state, action: PayloadAction<string | null>) => {
      state.loadingFareId = action.payload;
    },

    /** Reset interstitial state to initial values */
    resetTemplateInterstitialState: () => initialTemplateInterstitialState,
  },
  extraReducers: (builder) => {
    // Fetch Flight Interstitial (from listing click)
    // Note: Popup handling is in popupsSlice
    builder
      .addCase(fetchFlightInterstitialThunk.fulfilled, (state, action) => {
        state.selectedFares = action.payload.selectedFares;
        state.selectedFareCombo = action.payload.selectedFareCombo;
        state.loadingFareId = null;
        // Cache the template data for back navigation
        state.templateData = action.payload.templateData;
      })
      .addCase(fetchFlightInterstitialThunk.rejected, (state) => {
        state.loadingFareId = null;
      });

    // Fetch Interstitial With Fare (fare change within interstitial)
    // Note: Popup handling is in popupsSlice
    builder
      .addCase(fetchInterstitialWithFareThunk.pending, (state, action) => {
        state.loadingFareId = action.meta.arg.fareId;
      })
      .addCase(fetchInterstitialWithFareThunk.fulfilled, (state, action) => {
        state.selectedFares = action.payload.selectedFares;
        state.selectedFareCombo = action.payload.selectedFareCombo;
        state.loadingFareId = null;
        // Cache the template data for back navigation
        state.templateData = action.payload.templateData;
      })
      .addCase(fetchInterstitialWithFareThunk.rejected, (state) => {
        state.loadingFareId = null;
      });

    // ============================================
    // Initialize State from Chat Stream Templates
    // ============================================

    builder.addCase(updateTemplateFromStream, (state, action) => {
      const { activeTemplate, templateData } = action.payload;

      if (!templateData) {
        return;
      }

      // Handle flights-review-interstitial template
      // TODO: get flights-review removed from CB
      if (activeTemplate === 'flights-review-interstitial') {
        applyInterstitialDataToState(state, templateData as FlightsReviewInterstitialTemplateData);
        // Cache the template data for back navigation
        state.templateData = templateData as FlightsReviewInterstitialTemplateData;
      }
    });

    // Handle setActiveTemplate (used when loading chat history or switching templates)
    // We are not reapplying the stored state of selected fares and selected fare combo, bcz selected fares and selected fare combo are updated in the stream itself.
    builder.addCase(setActiveTemplate, (state, action) => {
      const { template, data } = action.payload;
      if (!data) {
        return;
      }
      // TODO: get flights-review removed from CB
      if (template === 'flights-review-interstitial' || template === 'flights-review') {
        applyInterstitialDataToState(state, data as FlightsReviewInterstitialTemplateData);
        state.templateData = data as FlightsReviewInterstitialTemplateData;
      }
    });
  },
});

// ============================================
// Actions
// ============================================

export const {
  setSelectedFares,
  setSelectedFareCombo,
  setLoadingFareId,
  resetTemplateInterstitialState,
} = templateInterstitialSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const templateInterstitialReducer = templateInterstitialSlice.reducer;
